import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileText, Eye, Download, BookOpen, Loader2, X, Users, DollarSign, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const DirectorLessonNotes = () => {
  const [activeView, setActiveView] = useState('notes');
  const [weeks, setWeeks] = useState([]);
  const [termInfo, setTermInfo] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [weekClasses, setWeekClasses] = useState({});
  const [expandedClass, setExpandedClass] = useState(null);
  const [classNotes, setClassNotes] = useState({});
  const [loadingWeeks, setLoadingWeeks] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [subAnalytics, setSubAnalytics] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);

  const fetchWeeks = useCallback(async () => {
    setLoadingWeeks(true);
    try {
      const res = await fetch(`${API_BASE}/api/director/lesson-notes/weeks`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setWeeks(data.weeks || []);
        setTermInfo(data.term);
      }
    } catch (e) { console.error(e); }
    setLoadingWeeks(false);
  }, []);

  useEffect(() => { fetchWeeks(); }, [fetchWeeks]);

  const toggleWeek = async (week) => {
    if (expandedWeek === week) {
      setExpandedWeek(null);
      setExpandedClass(null);
      return;
    }
    setExpandedWeek(week);
    setExpandedClass(null);

    if (!weekClasses[week]) {
      setLoadingClasses(week);
      try {
        const res = await fetch(`${API_BASE}/api/director/lesson-notes/week/${week}/classes`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setWeekClasses(prev => ({ ...prev, [week]: data.classes || [] }));
        }
      } catch (e) { console.error(e); }
      setLoadingClasses(null);
    }
  };

  const toggleClass = async (week, className) => {
    const key = `${week}-${className}`;
    if (expandedClass === key) {
      setExpandedClass(null);
      return;
    }
    setExpandedClass(key);

    if (!classNotes[key]) {
      setLoadingNotes(key);
      try {
        const res = await fetch(`${API_BASE}/api/director/lesson-notes/week/${week}/class/${encodeURIComponent(className)}`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setClassNotes(prev => ({ ...prev, [key]: data.notes || [] }));
        }
      } catch (e) { console.error(e); }
      setLoadingNotes(null);
    }
  };

  const handleView = async (noteId) => {
    setViewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/director/lesson-notes/view/${noteId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setViewNote(data);
      } else {
        toast.error('Failed to load lesson note');
      }
    } catch (e) { toast.error('Error loading note'); }
    setViewLoading(false);
  };

  const handleDownload = async (noteId, title) => {
    setDownloading(noteId);
    try {
      const res = await fetch(`${API_BASE}/api/director/lesson-note-download/${noteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title || 'lesson_note'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Lesson note downloaded');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Download failed');
      }
    } catch (e) { toast.error('Download error'); }
    setDownloading(null);
  };

  const fetchSubAnalytics = useCallback(async () => {
    setLoadingSubs(true);
    try {
      const res = await fetch(`${API_BASE}/api/director/lesson-notes/subscription-analytics`, { headers: getAuthHeaders() });
      if (res.ok) {
        setSubAnalytics(await res.json());
      }
    } catch (e) { console.error(e); }
    setLoadingSubs(false);
  }, []);

  return (
    <div className="space-y-4" data-testid="director-lesson-notes">
      {/* View Toggle */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant={activeView === 'notes' ? 'default' : 'outline'}
          onClick={() => setActiveView('notes')}
          className={activeView === 'notes' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          data-testid="lesson-notes-view-btn"
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Teacher Lesson Notes
        </Button>
        <Button
          variant={activeView === 'subscriptions' ? 'default' : 'outline'}
          onClick={() => { setActiveView('subscriptions'); if (!subAnalytics) fetchSubAnalytics(); }}
          className={activeView === 'subscriptions' ? 'bg-green-600 hover:bg-green-700' : ''}
          data-testid="teacher-subscription-btn"
        >
          <Users className="h-4 w-4 mr-2" />
          Teacher Subscription
        </Button>
      </div>

      {/* Teacher Subscription Analytics View */}
      {activeView === 'subscriptions' && (
        <Card className="border-green-200">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">District Subscription Revenue Analysis</CardTitle>
            </div>
            <CardDescription>
              Lesson note subscription analytics for your district
              {subAnalytics?.active_term && (
                <span className="block mt-1 font-medium text-green-700">
                  {subAnalytics.active_term.name} — {subAnalytics.active_term.academic_year}
                </span>
              )}
              {!subAnalytics?.active_term && !loadingSubs && (
                <span className="block mt-1 font-medium text-red-600">No active term — all figures reset to zero</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSubs ? (
              <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading analytics...
              </div>
            ) : subAnalytics ? (
              <div className="space-y-4">
                {/* District Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 text-center">
                    <p className="text-xs text-blue-600 font-medium mb-1">District</p>
                    <p className="text-sm font-bold text-blue-900">{subAnalytics.district_name}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200 text-center">
                    <p className="text-xs text-green-600 font-medium mb-1">Subscribed Teachers</p>
                    <p className="text-2xl font-bold text-green-800">{subAnalytics.active_subscriptions}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-center">
                    <p className="text-xs text-amber-600 font-medium mb-1">Amount Per Subscription</p>
                    <p className="text-2xl font-bold text-amber-800">GHS {(subAnalytics.amount_per_subscription || 0).toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 text-center">
                    <p className="text-xs text-purple-600 font-medium mb-1">Total Revenue</p>
                    <p className="text-2xl font-bold text-purple-800">GHS {(subAnalytics.total_revenue || 0).toFixed(2)}</p>
                    <p className="text-[10px] text-purple-500 mt-1">{subAnalytics.active_subscriptions} x GHS {(subAnalytics.amount_per_subscription || 0).toFixed(2)}</p>
                  </div>
                </div>

                {/* Recent Subscriptions Table */}
                {subAnalytics.recent_subscriptions?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Active Subscribed Teachers</h4>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left text-xs text-gray-500">
                            <th className="p-3">Teacher</th>
                            <th className="p-3">School</th>
                            <th className="p-3">Subjects</th>
                            <th className="p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subAnalytics.recent_subscriptions.map((sub, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-3">
                                <p className="font-medium text-gray-800">{sub.teacher_name}</p>
                                <p className="text-xs text-gray-400">{sub.teacher_email}</p>
                              </td>
                              <td className="p-3 text-gray-600">{sub.school || '-'}</td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {(sub.classes_subjects || []).map((cs, j) => (
                                    <Badge key={j} variant="outline" className="text-xs">{typeof cs === 'object' ? cs.subject || cs.class_name : cs}</Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="p-3">
                                <Badge className={sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                                  {sub.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {subAnalytics.recent_subscriptions?.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No active subscriptions in your district yet.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No data available</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lesson Notes View */}
      {activeView === 'notes' && (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Teacher Lesson Notes</CardTitle>
          </div>
          <CardDescription>
            Browse and download officially published weekly lesson notes for supervision and monitoring.
            {termInfo && (
              <span className="block mt-1 font-medium text-gray-700">
                {termInfo.academic_year} — {termInfo.name} (Current Week: {termInfo.current_week})
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingWeeks ? (
            <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading weeks...
            </div>
          ) : weeks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No lesson notes available for this term.</p>
          ) : (
            <div className="space-y-1" data-testid="weeks-folder">
              {weeks.map(week => (
                <div key={week}>
                  {/* Week Row */}
                  <button
                    onClick={() => toggleWeek(week)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-blue-50 transition text-left group"
                    data-testid={`week-folder-${week}`}
                  >
                    {expandedWeek === week ? (
                      <ChevronDown className="h-4 w-4 text-blue-500 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                    {expandedWeek === week ? (
                      <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
                    ) : (
                      <Folder className="h-4 w-4 text-amber-400 shrink-0" />
                    )}
                    <span className="font-medium text-sm text-gray-800 group-hover:text-blue-700">
                      Week {week}
                    </span>
                    {loadingClasses === week && <Loader2 className="h-3 w-3 animate-spin text-blue-400 ml-2" />}
                  </button>

                  {/* Classes under this week */}
                  {expandedWeek === week && weekClasses[week] && (
                    <div className="ml-6 pl-3 border-l-2 border-blue-100 space-y-1 mt-1">
                      {weekClasses[week].length === 0 ? (
                        <p className="text-xs text-gray-400 py-1 pl-2">No classes available</p>
                      ) : weekClasses[week].map(cls => {
                        const classKey = `${week}-${cls}`;
                        return (
                          <div key={cls}>
                            <button
                              onClick={() => toggleClass(week, cls)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-green-50 transition text-left group"
                              data-testid={`class-folder-${week}-${cls}`}
                            >
                              {expandedClass === classKey ? (
                                <ChevronDown className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                              )}
                              {expandedClass === classKey ? (
                                <FolderOpen className="h-4 w-4 text-green-500 shrink-0" />
                              ) : (
                                <Folder className="h-4 w-4 text-green-400 shrink-0" />
                              )}
                              <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">{cls}</span>
                              {loadingNotes === classKey && <Loader2 className="h-3 w-3 animate-spin text-green-400 ml-2" />}
                            </button>

                            {/* Note Cards */}
                            {expandedClass === classKey && classNotes[classKey] && (
                              <div className="ml-6 pl-3 border-l-2 border-green-100 space-y-2 mt-1 mb-2">
                                {classNotes[classKey].length === 0 ? (
                                  <p className="text-xs text-gray-400 py-1">No lesson notes for this class and week</p>
                                ) : classNotes[classKey].map(note => (
                                  <div
                                    key={note.id}
                                    className="flex items-center justify-between p-3 bg-white border rounded-lg shadow-sm hover:shadow transition"
                                    data-testid={`note-card-${note.id}`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">
                                          {note.title || note.file_name || note.subject}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                          {note.subject} | {cls} | Week {week}
                                          {note.file_name && <span className="ml-1 text-gray-400">• {note.file_name}</span>}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0">
                                      <Button
                                        size="sm" variant="outline"
                                        className="h-7 text-xs"
                                        onClick={() => handleView(note.id)}
                                        data-testid={`view-note-${note.id}`}
                                      >
                                        <Eye className="h-3 w-3 mr-1" /> View
                                      </Button>
                                      <Button
                                        size="sm" variant="outline"
                                        className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                        onClick={() => handleDownload(note.id, note.title || `${cls}_${note.subject}_Week${week}`)}
                                        disabled={downloading === note.id}
                                        data-testid={`download-note-${note.id}`}
                                      >
                                        {downloading === note.id ? (
                                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                        ) : (
                                          <Download className="h-3 w-3 mr-1" />
                                        )}
                                        Download
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewNote} onOpenChange={() => setViewNote(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-500" />
              {viewNote?.title || viewNote?.file_name || 'Lesson Note'}
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : viewNote?.content_html ? (
            <div className="prose prose-sm max-w-none">
              <div className="flex gap-2 mb-3 flex-wrap">
                <Badge variant="outline">{viewNote.class_name}</Badge>
                <Badge variant="outline">{viewNote.subject}</Badge>
                <Badge variant="outline">Week {viewNote.week}</Badge>
                {viewNote.term && <Badge variant="outline">Term {viewNote.term}</Badge>}
              </div>
              <div
                className="border rounded-lg p-4 bg-gray-50 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: viewNote.content_html }}
              />
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No content available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectorLessonNotes;
